import { type ReactNode, useEffect, useId, useState } from 'react';

import { match } from 'ts-pattern';

import { CheckboxField } from '@nesso/ui/primitives/checkbox-field';

import { type CheckboxesListOption, CheckboxList } from './checkbox-list';
import { CommentableCheckbox } from './checkbox-list-with-other/commentable-checkbox';

const OTHER_LABEL = 'Other';

// Returns the trimmed value, or undefined when blank/whitespace-only — so a blank
// "Other" never surfaces as a checkbox with an invisible label in showOnlySelected
// mode, and the label never renders with stray surrounding whitespace.
const nonBlank = (value?: string): string | undefined => value?.trim() || undefined;

export type Props = {
    options: CheckboxesListOption[];
    values: string[];
    onValuesChange: (values: string[]) => void;
    columns?: 1 | 2;
    showOnlySelected?: boolean;
    noWrapper?: boolean;
    otherValue?: string;
    onOtherValueChange: (value: string | undefined) => void;
};

export function CheckboxListWithOther({
    otherValue,
    onOtherValueChange,
    showOnlySelected,
    ...rest
}: Props) {
    const otherId = useId();
    const [otherChecked, setOtherChecked] = useState(!!nonBlank(otherValue));
    // Local stash so unchecking inside showOnlySelected can clear the parent (fires
    // onOtherValueChange(undefined)) while keeping the row + typed text on screen until
    // the user re-checks or leaves the mode. Cleared on transition to expanded view.
    const [stashedValue, setStashedValue] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!showOnlySelected && !otherChecked) setStashedValue(undefined);
    }, [showOnlySelected, otherChecked]);

    const handleExpandedToggle = (next: boolean) => {
        setOtherChecked(next);
    };

    const handleVisibleToggle = (next: boolean) => {
        setOtherChecked(next);
        if (next) {
            if (stashedValue) onOtherValueChange(stashedValue);
        } else {
            setStashedValue(nonBlank(otherValue));
            onOtherValueChange(undefined);
        }
    };

    const otherVisible = !!nonBlank(otherValue) || !!nonBlank(stashedValue);

    const otherSlot: ReactNode = match({ showOnlySelected: !!showOnlySelected, otherVisible })
        .with({ showOnlySelected: false }, () => (
            <CommentableCheckbox
                id={otherId}
                checked={otherChecked}
                onCheckedChange={handleExpandedToggle}
                comment={otherValue}
                onCommentChange={onOtherValueChange}
            />
        ))
        .with({ otherVisible: true }, () => (
            <CheckboxField
                id={otherId}
                label={nonBlank(otherValue) ?? nonBlank(stashedValue) ?? OTHER_LABEL}
                checked={otherChecked}
                onCheckedChange={(value) => handleVisibleToggle(value === true)}
            />
        ))
        .otherwise(() => null);

    return <CheckboxList {...rest} showOnlySelected={showOnlySelected} extras={otherSlot} />;
}
